/**
 * アイテム・ガチャカタログ(docs/04_data_model.md §3 / docs/02_game_design.md §7)。
 *
 * `content/v1/catalog/{items,gachas}.json`(tools/content-build の成果物)を読む。
 * **排出率・価格・重複変換額はすべてこのデータから来る**(コード側に定数を持たない)。
 *
 * アート素材はまだ無いので、アイテムの見た目は `emoji`(+ レアリティ由来のCSS装飾)で表す。
 * 画像ができたら `image` が入り、UI はそちらを優先する。
 */

import type { ScoreModifier, ScoreModifierType } from '@paltyping/engine';

/** カタログのスキーマ違反。 */
export class CatalogError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CatalogError';
  }
}

export type Rarity = 'N' | 'R' | 'SR' | 'UR';
/** レアリティの昇順(「R以上保証」の判定に使う)。 */
export const RARITY_ORDER: readonly Rarity[] = ['N', 'R', 'SR', 'UR'];

export type ItemSlot = 'hat' | 'face' | 'outfit' | 'hand' | 'background' | 'effect';
export const ITEM_SLOTS: readonly ItemSlot[] = [
  'hat',
  'face',
  'outfit',
  'hand',
  'background',
  'effect',
];

/** engine が解釈できる効果タイプ。これ以外はパース時に捨てる。 */
const EFFECT_TYPES: readonly ScoreModifierType[] = [
  'gaugeDecayReduction',
  'comboCapUp',
  'missForgive',
  'coinBonus',
];

/**
 * アイテムの出どころ(docs/12_challenge_ranking.md §3)。
 *
 * `challenge` = チャレンジのトロフィーコインでしか手に入らないもの。
 * **将来の装備ゲート(docs/10 §4.3)の対象外**という印で、
 * 「買えるものは課金、勝ち取るものは実力」を機械的に判別するために持つ。
 * 知らない値が来たら `gacha` として扱う(画面は壊さない)。
 */
export type ItemOrigin = 'gacha' | 'challenge';

export interface CatalogItem {
  readonly id: string;
  readonly slot: ItemSlot;
  readonly rarity: Rarity;
  readonly name: string;
  /** アート未整備のあいだの暫定表現。 */
  readonly emoji: string;
  /** 画像ができたらこちらを優先する(コンテンツ相対パス)。 */
  readonly image?: string;
  /** 出どころ。カタログに無ければ `gacha`。 */
  readonly origin: ItemOrigin;
  readonly effects: readonly ScoreModifier[];
}

export interface GachaPoolEntry {
  readonly itemId: string;
  readonly weight: number;
  /**
   * **排出できる月**(`YYYY-MM`。docs/12 §3 の月替わり限定)。
   *
   * 省略は恒常枠。指定があるものは当月だけ抽選対象に入る。
   * **実際に何が出るかを決めるのはサーバー**で、ここは
   * 「こんげつの げんてい」の表示と排出率の内訳に使う。
   */
  readonly months?: readonly string[];
}

export interface GachaDefinition {
  readonly id: string;
  readonly name: string;
  /**
   * 支払いに使う通貨(docs/10 §4.3「モジュール別ガチャ」)。
   *
   * `typing` = タイピングのコイン / 教科名のローマ字 = まなびの教科コイン。
   * **実際に引き落とす通貨を決めるのはサーバー**で、ここは表示の出し分け用。
   */
  readonly currency: string;
  /** 単発コスト。 */
  readonly cost: number;
  /** 連ガチャのコスト(guarantee.count 回ぶん)。 */
  readonly cost10: number;
  /** レアリティ排出率(合計 1.0)。表示・抽選の唯一の出どころ。 */
  readonly rarityRates: Readonly<Record<Rarity, number>>;
  /** 連数と保証レアリティ。 */
  readonly guarantee: { readonly count: number; readonly minRarity: Rarity };
  /** 重複時の自動コイン変換額。 */
  readonly duplicateConvertCoins: Readonly<Record<Rarity, number>>;
  readonly pool: readonly GachaPoolEntry[];
  readonly period: { readonly from: string | null; readonly to: string | null };
}

/** カタログ一式(id 引き済み)。 */
export interface Catalog {
  readonly items: readonly CatalogItem[];
  readonly gachas: readonly GachaDefinition[];
  readonly itemsById: ReadonlyMap<string, CatalogItem>;
}

// ── パース ────────────────────────────────────────────────

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new CatalogError(`${where}: オブジェクトではありません`);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, where: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new CatalogError(`${where}: 文字列ではありません`);
  }
  return value;
}

function asNumber(value: unknown, where: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new CatalogError(`${where}: 数値ではありません`);
  }
  return value;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) throw new CatalogError(`${where}: 配列ではありません`);
  return value;
}

function parseRarityMap(value: unknown, where: string): Record<Rarity, number> {
  const raw = asRecord(value, where);
  const result = {} as Record<Rarity, number>;
  for (const rarity of RARITY_ORDER) {
    result[rarity] = asNumber(raw[rarity], `${where}.${rarity}`);
  }
  return result;
}

export function parseItem(raw: unknown, where: string): CatalogItem {
  const item = asRecord(raw, where);
  const slot = asString(item.slot, `${where}.slot`);
  if (!(ITEM_SLOTS as readonly string[]).includes(slot)) {
    throw new CatalogError(`${where}.slot: 未知のスロット "${slot}"`);
  }
  const rarity = asString(item.rarity, `${where}.rarity`);
  if (!(RARITY_ORDER as readonly string[]).includes(rarity)) {
    throw new CatalogError(`${where}.rarity: 未知のレアリティ "${rarity}"`);
  }
  const id = asString(item.id, `${where}.id`);
  const effects: ScoreModifier[] = [];
  for (const [i, entry] of asArray(item.effects ?? [], `${where}.effects`).entries()) {
    const effect = asRecord(entry, `${where}.effects[${i}]`);
    const type = asString(effect.type, `${where}.effects[${i}].type`);
    // engine が知らない効果は将来追加ぶん。落として動かす(壊さない)。
    if (!(EFFECT_TYPES as readonly string[]).includes(type)) continue;
    effects.push({
      type: type as ScoreModifierType,
      value: asNumber(effect.value, `${where}.effects[${i}].value`),
      itemId: id,
    });
  }
  const image = typeof item.image === 'string' && item.image !== '' ? item.image : undefined;
  return {
    id,
    slot: slot as ItemSlot,
    rarity: rarity as Rarity,
    name: asString(asRecord(item.name, `${where}.name`).ja, `${where}.name.ja`),
    emoji: asString(item.emoji, `${where}.emoji`),
    ...(image === undefined ? {} : { image }),
    // 知らない出どころは `gacha` 扱い(表示にしか使わないので画面は壊さない)。
    origin: item.origin === 'challenge' ? 'challenge' : 'gacha',
    effects,
  };
}

export function parseGacha(raw: unknown, where: string): GachaDefinition {
  const gacha = asRecord(raw, where);
  const guarantee = asRecord(gacha.guarantee, `${where}.guarantee`);
  const minRarity = asString(guarantee.minRarity, `${where}.guarantee.minRarity`);
  if (!(RARITY_ORDER as readonly string[]).includes(minRarity)) {
    throw new CatalogError(`${where}.guarantee.minRarity: 未知のレアリティ "${minRarity}"`);
  }
  const period = asRecord(gacha.period ?? { from: null, to: null }, `${where}.period`);
  return {
    id: asString(gacha.id, `${where}.id`),
    name: asString(asRecord(gacha.name, `${where}.name`).ja, `${where}.name.ja`),
    // 通貨が無いカタログ(教科ガチャ以前)は typing とみなす。
    currency: typeof gacha.currency === 'string' ? gacha.currency : 'typing',
    cost: asNumber(gacha.cost, `${where}.cost`),
    cost10: asNumber(gacha.cost10, `${where}.cost10`),
    rarityRates: parseRarityMap(gacha.rarityRates, `${where}.rarityRates`),
    guarantee: {
      count: asNumber(guarantee.count, `${where}.guarantee.count`),
      minRarity: minRarity as Rarity,
    },
    duplicateConvertCoins: parseRarityMap(
      gacha.duplicateConvertCoins,
      `${where}.duplicateConvertCoins`,
    ),
    pool: asArray(gacha.pool, `${where}.pool`).map((entry, i) => {
      const p = asRecord(entry, `${where}.pool[${i}]`);
      const months =
        p.months === undefined
          ? undefined
          : asArray(p.months, `${where}.pool[${i}].months`).map((m, j) =>
              asString(m, `${where}.pool[${i}].months[${j}]`),
            );
      return {
        itemId: asString(p.itemId, `${where}.pool[${i}].itemId`),
        weight: asNumber(p.weight, `${where}.pool[${i}].weight`),
        ...(months === undefined ? {} : { months }),
      };
    }),
    period: {
      from: typeof period.from === 'string' ? period.from : null,
      to: typeof period.to === 'string' ? period.to : null,
    },
  };
}

/** `catalog/items.json` + `catalog/gachas.json` を1つの Catalog にまとめる。 */
export function buildCatalog(rawItems: unknown, rawGachas: unknown): Catalog {
  const itemsRoot = asRecord(rawItems, 'items');
  const gachasRoot = asRecord(rawGachas, 'gachas');
  const items = asArray(itemsRoot.items, 'items.items').map((raw, i) =>
    parseItem(raw, `items[${i}]`),
  );
  const gachas = asArray(gachasRoot.gachas, 'gachas.gachas').map((raw, i) =>
    parseGacha(raw, `gachas[${i}]`),
  );
  const itemsById = new Map(items.map((item) => [item.id, item]));
  for (const gacha of gachas) {
    for (const entry of gacha.pool) {
      if (!itemsById.has(entry.itemId)) {
        throw new CatalogError(
          `ガチャ ${gacha.id}: アイテム ${entry.itemId} がカタログにありません`,
        );
      }
    }
  }
  return { items, gachas, itemsById };
}

/** 空のカタログ(読み込み前の初期値)。 */
export const EMPTY_CATALOG: Catalog = { items: [], gachas: [], itemsById: new Map() };
